"use client"

import { Head, router } from "@inertiajs/react"
import { AlertCircle, ArrowRight, ClockFastForward } from "@untitledui/icons"
import { useState } from "react"

import { batyrTwoFacesSmileBlob as fallbackImage } from "@/assets/images/photos"
import { SplitImageWrapper } from "@/components/auth"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { BatyrLogo, BatyrLogoVariant } from "@/components/ui/logo-batyr"
import { AuthSplitLayout } from "@/layouts/auth/auth-split-layout"
import { confirmWarmStartClaimPath, rootPath } from "@/routes"

type ClaimState = "valid" | "expired" | "invalid"

interface ClaimPreview {
  title: string
  track_count: number
}

interface ClaimPageProps {
  state: ClaimState
  token?: string
  recipient_name?: string | null
  inviter_name?: string | null
  inviter_avatar_url?: string | null
  artifact_kind?: string
  preview?: ClaimPreview | null
}

export default function WarmStartClaim({
  state,
  token,
  recipient_name,
  inviter_name,
  inviter_avatar_url,
  preview,
}: ClaimPageProps) {
  return (
    <AuthSplitLayout sidebar={<SplitImageWrapper src={fallbackImage} />}>
      <Head title="Your invitation" />
      <header className="flex justify-center">
        <BatyrLogo variant={BatyrLogoVariant.ICON} className="h-12 w-auto" />
      </header>

      {state === "valid" ? (
        <ValidLanding
          token={token!}
          recipientName={recipient_name}
          inviterName={inviter_name}
          inviterAvatarUrl={inviter_avatar_url}
          preview={preview}
        />
      ) : (
        <UnavailableLanding state={state} />
      )}
    </AuthSplitLayout>
  )
}

// "Cal Whitmore" → "Cal". Defensive against blank/whitespace-only names so the
// greeting and signature never render a dangling "Hey !" or "— ".
function firstName(full?: string | null): string {
  return full?.trim().split(/\s+/)[0] ?? ""
}

function ValidLanding({
  token,
  recipientName,
  inviterName,
  inviterAvatarUrl,
  preview,
}: {
  token: string
  recipientName?: string | null
  inviterName?: string | null
  inviterAvatarUrl?: string | null
  preview?: ClaimPreview | null
}) {
  // No auto-submit on mount: the click is the prefetch gate. Email scanners
  // GET the landing and don't run JS, so the sign-in only fires on a real tap.
  const [processing, setProcessing] = useState(false)

  const openPlaylist = () => {
    router.post(
      confirmWarmStartClaimPath(token),
      {},
      {
        onStart: () => setProcessing(true),
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 text-left">
        <h1 className="text-display-sm text-primary font-semibold text-balance">
          Hey {firstName(recipientName) || "there"}!
        </h1>

        {inviterName && (
          <div className="flex items-center gap-3">
            <Avatar name={inviterName} avatarUrl={inviterAvatarUrl} size="md" />
            <div className="min-w-0">
              <p className="text-primary text-sm font-semibold">
                {inviterName}
              </p>
              <p className="text-tertiary text-sm">batyr team member</p>
            </div>
          </div>
        )}

        <p className="text-secondary text-md">
          I pre-filled a draft playlist based on the highlights from our chat.
          We&rsquo;d love to feature your voice on the platform!
        </p>

        {inviterName && (
          <p className="text-tertiary text-md">
            &mdash; {firstName(inviterName)}
          </p>
        )}
      </div>

      {preview && (
        <div className="border-secondary bg-primary flex flex-col gap-1 rounded-2xl border p-5 shadow-xs">
          <span className="text-tertiary text-xs font-semibold tracking-wide uppercase">
            Your playlist
          </span>
          <p className="text-primary text-lg font-semibold">{preview.title}</p>
          <p className="text-tertiary text-sm">
            {preview.track_count}{" "}
            {preview.track_count === 1 ? "track" : "tracks"}
          </p>
        </div>
      )}

      <Button
        size="lg"
        onClick={openPlaylist}
        isDisabled={processing}
        isLoading={processing}
        showTextWhileLoading
        iconTrailing={<ArrowRight />}
      >
        Open my playlist
      </Button>
    </div>
  )
}

function UnavailableLanding({ state }: { state: ClaimState }) {
  const expired = state === "expired"

  return (
    <EmptyState size="md">
      <EmptyState.Header pattern="none">
        <EmptyState.FeaturedIcon
          color="gray"
          theme="modern"
          icon={expired ? ClockFastForward : AlertCircle}
        />
      </EmptyState.Header>
      <EmptyState.Content>
        <p className="text-primary text-md text-center font-semibold">
          {expired ? "This invite has expired" : "This link isn’t valid"}
        </p>
        <EmptyState.Description>
          {expired
            ? "The window to claim this one has closed. Reach out to whoever sent it and they can send a fresh link."
            : "We couldn’t open this invite. Double-check the link, or ask whoever sent it to send a new one."}
        </EmptyState.Description>
      </EmptyState.Content>
      <EmptyState.Footer>
        <Button
          color="secondary"
          size="md"
          onClick={() => router.visit(rootPath())}
        >
          Go to Lists
        </Button>
      </EmptyState.Footer>
    </EmptyState>
  )
}
